import { useCallback, useEffect, useState } from "react";
import { Alert, Avatar, Button, Drawer, Modal, Select, Space } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { useAppSelector } from "redux/hooks";
import { isEmpty, isEqual, sortBy } from "lodash";
import { useDispatch } from "react-redux";
import { getAvatarColorsByAlpha } from "utils/Colors";
import { AppRolesValuesType, RoleType, StaffUserType } from "utils/Types";
import { API_RESPONSE_STATUS, APP_ROLES } from "utils/Constants";
import api from "ApiClient";
import { ApiURL } from "utils/Actions";
import { fetchRaters } from "redux/reducers/CredentialsSlice";
import { showErrorNotification, showSuccessNotification } from "utils/hooks/handleNotification";
import ReadOnlyValue from "components/common/read-only-value";
import AuthHelper from "components/auth/helpers";
import MakeOwner from "../modals/MakeOwner";
import ConfirmRole from "../modals/ConfirmRole";
import { ROLE_OPTION } from "utils/AssetsData";
import { allocateOptions } from "utils/hooks/allocateOptions";
import { initialName } from "utils/Format";

const { Option } = Select;

const sortRoles = (input: AppRolesValuesType[]) => {
  let sortedRoles: AppRolesValuesType[] = [];
  if (!isEmpty(input)) {
    sortedRoles = sortBy(input, a => {
      if (a === APP_ROLES.ADMIN) {
        return -1;
      } else {
        return 1;
      }
    });
    return sortedRoles;
  }
};

interface StaffDetailsProps {
  open: boolean;
  close: () => void;
  staff: StaffUserType | undefined;
  setDisplayStaffMember: (param: undefined | StaffUserType) => void;
}

const StaffDetails = ({
  open,
  close,
  staff,
  setDisplayStaffMember
}: StaffDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [confirmMakeOwner, setConfirmMakeOwner] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string[]>([]);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [confirmRoleVisible, setConfirmRoleVisible] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(false);
  const { whiteLabel = "" } = useAppSelector(state => state.properties);
  const [roleSelectOption, setRoleSelectOption] = useState<RoleType[]>([]);
  const staffIsAdmin = staff?.roles?.includes(APP_ROLES.ADMIN);

  useEffect(() => {
    if (staff) {
      setSelectedRoles(staff.roles);
    }
  }, [staff]);

  useEffect(() => {
    setRoleSelectOption(ROLE_OPTION[whiteLabel]);
    updateOptionsForAdmin();
  }, [whiteLabel, staff]);

  const updateOptionsForAdmin = useCallback(() => {
    if (staffIsAdmin) {
      const admin: RoleType = {
        value: APP_ROLES.ADMIN,
        labelKey: "roles.ADMIN",
        disabled: true
      };
      setRoleSelectOption([admin, ...roleSelectOption]);
    }
  }, [staff]);

  const [selectedRoles, setSelectedRoles] = useState<AppRolesValuesType[]>([]);

  const handleAfterOpenChange = useCallback(open => {
    if (!open) {
      setDisplayStaffMember(undefined);
      setDeleteError([]);
    }
  }, []);

  const handleDeleteStaff = async () => {
    setDeleting(true);
    try {
      const result = await api.delete(ApiURL.deleteStaff, {
        data: {
          email: staff?.email,
          seqNum: staff?.seqNum
        }
      });
      if (result.data?.status === API_RESPONSE_STATUS.SUCCESS) {
        handleClose();
        setConfirmDeleteVisible(false);
        dispatch(fetchRaters());
        showSuccessNotification({
          message: t("deleteStaff.successMsg", { name: `${staff?.firstName} ${staff?.lastName}` })
        });
        setDeleteError([]);
      } else {
        setDeleteError(result.data.errors);
        throw new Error(result.data.errors);
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const displayedRoles = sortRoles(selectedRoles);

  const handleClickMakeOwner = useCallback(() => {
    setConfirmMakeOwner(true);
  }, [staff]);

  if (staff === undefined) {
    return <></>;
  }

  const {
    firstName = "",
    lastName = "",
    email = "",
    seqNum,
    position = "-",
    address1 = "-",
    address2 = "-",
    address3 = "-",
    passwordSet
  } = staff;
  const [foregroundColor, backgroundColor] = getAvatarColorsByAlpha(firstName);

  const displayedName = [firstName, lastName].join(" ").trim();

  const handleClose = () => {
    if (deleting) {
      return;
    }
    close();
  };

  const handleDiscard = () => {
    showSuccessNotification({ message: "Changes discarded" });
    setSelectedRoles([]);
    handleClose();
  };

  const handleSelectRoleChange = (values: AppRolesValuesType[]) => {
    setSelectedRoles(values);
  };

  const handleSave = async () => {
    if (isYou) {
      setConfirmRoleVisible(true);
    } else {
      setUpdatingRole(true);
      try {
        const result = await api.put(ApiURL.updateRole, {
          email: email,
          seqNum: seqNum,
          roles: selectedRoles
        });
        if (result.data.status === API_RESPONSE_STATUS.SUCCESS) {
          showSuccessNotification({ message: "Changes saved" });
          handleClose();
        } else {
          setDeleteError(result.data.errors);
          throw new Error(result.data.errors);
        }
      } catch (error) {
        console.error({ error });
        showErrorNotification({ message: "Unable to save changes" });
      } finally {
        setUpdatingRole(false);
        dispatch(fetchRaters());
      }
    }
  };

  const saveDisabled = isEqual(staff.roles, selectedRoles) || isEmpty(selectedRoles);

  const actions = [
    <Button
      key="save"
      type="primary"
      disabled={saveDisabled}
      style={{ marginRight: 8 }}
      onClick={handleSave}
      loading={updatingRole}
    >
      {t("staffDetails.saveCTA")}
    </Button>
  ];
  if (!saveDisabled) {
    actions.push(
      <Button key="discard" onClick={handleDiscard} disabled={updatingRole}>
        {t("staffDetails.discardCTA")}
      </Button>
    );
  }

  const cancelDelete = () => setConfirmDeleteVisible(false);

  const accepted = passwordSet === true;
  const townOrSuburb = [address2, address3].filter(add => add !== " ").join(",");
  const currentUserEmail = AuthHelper.getEmail();
  const isYou = currentUserEmail.toLowerCase() === email.toLowerCase();

  return (
    <>
      {/* modals */}
      <Modal
        title={t("deleteStaff.heading")}
        open={confirmDeleteVisible}
        closable={!deleting}
        zIndex={1001}
        onCancel={cancelDelete}
        maskClosable={false}
        footer={[
          <Button
            key="cancel"
            disabled={deleting}
            style={{ marginRight: 8 }}
            onClick={cancelDelete}
          >
            Cancel
          </Button>,
          <Button
            key="Delete staff member"
            danger
            type="primary"
            onClick={handleDeleteStaff}
            loading={deleting}
            disabled={deleting}
          >
            {t("staffDetails.deleteStaffCTA")}
          </Button>
        ]}
      >
        {!isEmpty(deleteError) && (
          <Alert
            message={deleteError.join(" ")}
            type="error"
            closable
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}
        <p>
          <Trans>{t("deleteStaff.description", { email: email })}</Trans>
        </p>
      </Modal>

      <MakeOwner
        staff={staff}
        open={confirmMakeOwner}
        onCancel={() => setConfirmMakeOwner(false)}
      />

      <ConfirmRole
        staff={staff}
        open={confirmRoleVisible}
        onCancel={() => setConfirmRoleVisible(false)}
        selectedRoles={selectedRoles}
      />

      <Drawer
        open={open}
        onClose={handleClose}
        title={t("staffDetails.heading")}
        footer={actions}
        afterOpenChange={handleAfterOpenChange}
        maskClosable={false}
      >
        <Space
          direction="vertical"
          size={16}
          align="center"
          style={{ width: "100%", marginBottom: 24 }}
        >
          <Avatar
            size={64}
            style={{ color: foregroundColor, background: backgroundColor }}
            aria-hidden={true}
          >
            {initialName(firstName)}
          </Avatar>
          <h1>{displayedName}</h1>
          {!isYou && (
            <Space>
              {accepted && (
                <Button onClick={handleClickMakeOwner}>{t("staffDetails.makeOwnerCTA")}</Button>
              )}
              <Button
                danger
                onClick={() => {
                  setConfirmDeleteVisible(true);
                }}
                loading={deleting}
              >
                <span style={{ color: "#D72C0D" }}>{t("staffDetails.deleteStaffCTA")}</span>
              </Button>
            </Space>
          )}
        </Space>
        <ReadOnlyValue label="Email" value={email} />
        <div className="info-item">
          <p className="display-p-typo" style={{ marginBottom: 4 }}>
            Role
          </p>
          <Select
            aria-label="Role"
            style={{ width: "100%" }}
            value={displayedRoles}
            mode="multiple"
            onChange={handleSelectRoleChange}
            getPopupContainer={allocateOptions}
          >
            {roleSelectOption.map((e: RoleType) => (
              <Option key={e.value} value={e.value} disabled={e.value === APP_ROLES.ADMIN}>
                {t(e.labelKey)}
              </Option>
            ))}
          </Select>
        </div>
        <ReadOnlyValue label="Position" value={position} />
        <ReadOnlyValue label="Address" value={address1} />
        <ReadOnlyValue label="Town or suburb" value={townOrSuburb} />
      </Drawer>
    </>
  );
};

export default StaffDetails;
